import api from './api';
import { AvailableVariable } from './emailTemplateService';

export type { AvailableVariable };

export interface PromptSection {
  name?: string;
  title: string;
  prompt: string;
}

export interface CriteriaItem {
  field: string;
  operator: string;
  value?: string;
}

export interface PromptCriteria {
  include: CriteriaItem[];
  exclude: CriteriaItem[];
}

export type PromptDeployment = 'dev' | 'pilot' | 'prod';
export type PromptProvider = 'openai' | 'gemini' | 'claude';
export type PromptThinkingLevel = 'minimal' | 'low' | 'medium' | 'high';

export const MODEL_OPTIONS: Record<string, string[]> = {
  openai: ['gpt-5.5', 'gpt-5.4-mini', 'gpt-5.4-nano', 'o3', 'o3-pro', 'o4-mini', 'o4-mini-high'],
  gemini: ['gemini-3.1-pro-preview', 'gemini-3.1-flash-lite-preview', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'],
  claude: ['claude-opus-4-7', 'claude-opus-4-5', 'claude-sonnet-4-6', 'claude-sonnet-4-5', 'claude-haiku-4-5-20251001'],
};

export interface Prompt {
  id: number;
  tenant_id: number | null;
  tenant_name?: string | null;
  prompt_type: string;
  name: string;
  general: string | null;
  sections: PromptSection[] | null;
  criteria: PromptCriteria | null;
  is_global: boolean;
  is_active: boolean;
  deployment: PromptDeployment;
  priority: number;
  model_provider: PromptProvider | null;
  model: string | null;
  thinking_level: PromptThinkingLevel | null;
  meeting_type_id?: number | null;
  summary_type_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface PromptCreate {
  tenant_id?: number | null;
  prompt_type: string;
  name: string;
  general?: string | null;
  sections?: PromptSection[] | null;
  criteria?: PromptCriteria | null;
  is_global?: boolean;
  is_active?: boolean;
  deployment?: PromptDeployment;
  priority?: number;
  model_provider?: PromptProvider | null;
  model?: string | null;
  thinking_level?: PromptThinkingLevel | null;
  meeting_type_id?: number | null;
  summary_type_id?: number | null;
}

export interface PromptUpdate {
  name?: string;
  general?: string | null;
  sections?: PromptSection[] | null;
  criteria?: PromptCriteria | null;
  is_active?: boolean;
  deployment?: PromptDeployment;
  priority?: number;
  model_provider?: PromptProvider | null;
  model?: string | null;
  thinking_level?: PromptThinkingLevel | null;
  meeting_type_id?: number | null;
  summary_type_id?: number | null;
}

class PromptService {
  async getPrompts(promptType?: string, isActive?: boolean): Promise<Prompt[]> {
    const params = new URLSearchParams();
    if (promptType) params.append('prompt_type', promptType);
    if (isActive !== undefined) params.append('is_active', String(isActive));

    const response = await api.get(`/prompts/?${params.toString()}`);
    return response.data.items;
  }

  async getPrompt(id: number): Promise<Prompt> {
    const response = await api.get(`/prompts/${id}`);
    return response.data;
  }

  async createPrompt(prompt: PromptCreate): Promise<Prompt> {
    const response = await api.post('/prompts/', prompt);
    return response.data;
  }

  async updatePrompt(id: number, prompt: PromptUpdate): Promise<Prompt> {
    const response = await api.put(`/prompts/${id}`, prompt);
    return response.data;
  }

  async deletePrompt(id: number): Promise<void> {
    await api.delete(`/prompts/${id}`);
  }

  async getPromptVariables(promptType: string): Promise<AvailableVariable[]> {
    const contextMap: Record<string, string> = {
      meeting_summary: 'MEETING',
      questionnaire_summary: 'PATIENT',
    };
    const context = contextMap[promptType];
    if (!context) return [];
    const response = await api.get(`/prompts/contexts/${context}/variables`);
    return response.data.variables;
  }

  async runQuestionnaireSummary(
    patientId: number,
    promptId: number,
    action: 'send_summary' | 'update_summary',
    overrides?: { model_provider?: string | null; model?: string | null; thinking_level?: string | null },
  ): Promise<{ job_id: number }> {
    const response = await api.post('/prompts/run-questionnaire-summary', {
      patient_id: patientId,
      prompt_id: promptId,
      action,
      ...overrides,
    });
    return response.data;
  }

  async runMeetingSummary(
    meetingId: number,
    promptId: number,
    action: 'update_summary' | 'send_summary',
    overrides?: { model_provider?: string | null; model?: string | null; thinking_level?: string | null },
  ): Promise<{ job_id: number }> {
    const response = await api.post('/prompts/run-meeting-summary', {
      meeting_id: meetingId,
      prompt_id: promptId,
      action,
      ...overrides,
    });
    return response.data;
  }

  async copyToTenant(sourcePromptId: number, tenantId: number): Promise<Prompt> {
    // Get the source prompt
    const sourcePrompt = await this.getPrompt(sourcePromptId);

    // Create a copy for the tenant
    const newPrompt: PromptCreate = {
      tenant_id: tenantId,
      prompt_type: sourcePrompt.prompt_type,
      name: `${sourcePrompt.name} (Copy)`,
      general: sourcePrompt.general,
      sections: sourcePrompt.sections,
      is_global: false,
      is_active: true,
    };

    return this.createPrompt(newPrompt);
  }
}

const promptService = new PromptService();
export default promptService;
